import React, { useState, useContext, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  Stack,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { isEmpty } from 'lodash';

import { AppContext } from 'views/App/context';
import { translate as t } from 'hooks/useTranslations';
import { useQueryClient } from 'react-query';
import useManageManual from '../_data_sources/useManageManual';
import useDraftManuals from '../_data_sources/useDraftManuals';
import useVerticals from 'data_sources/useVerticals';
import useManualTypes from 'data_sources/useManualTypes';
import { Form } from 'components/_forms';
import { TakeoverLayout } from '_lex/_layouts/TakeoverLayout';
import errorHelper from 'helpers/errorHelper';
import { ServerErrorResponse } from 'data_sources/constants';
import { enqueueSnackbar } from 'notistack';
import { CreateAgencyManualRequest } from '../_data_sources/useManageManual';
import { Manual } from 'views/App/Portal/Policy/_types/manuals/draft';

const CreateAgencyManual: React.FC = () => {
  const navigate = useNavigate();
  const { currentOrganizationId, userOrganizationId } = useContext(AppContext);

  const [selectedManualId, setSelectedManualId] = useState('');
  const [title, setTitle] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [serverErrors, setServerErrors] = useState<ServerErrorResponse>();

  const queryClient = useQueryClient();
  const { createAgencyManual } = useManageManual();
  const draftManualsQuery = useDraftManuals();

  // Get all draft manuals and filter for jurisdiction manuals
  const jurisdictionManuals = useMemo(() => {
    if (!draftManualsQuery.data) return [];
    const allManuals = draftManualsQuery.data.pages.flatMap((page) => page.content);
    // Filter for jurisdiction manuals (belong to Lexipol and are not global)
    return allManuals.filter((manual: Manual) => {
      const isGlobalManual = manual.name.toLowerCase().includes('global');
      return manual.organizationId === 'lexipoladminstaffuuid' && !isGlobalManual;
    });
  }, [draftManualsQuery.data]);

  // Get the selected jurisdiction manual data
  const selectedManual = useMemo(() => {
    return jurisdictionManuals.find((manual) => manual.id === selectedManualId);
  }, [jurisdictionManuals, selectedManualId]);

  // Set default title when jurisdiction manual is selected
  useEffect(() => {
    if (selectedManual?.name && !title) {
      setTitle(selectedManual.name);
    }
  }, [selectedManual?.name, title]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedManualId || !title) {
      return;
    }

    const payload: CreateAgencyManualRequest = {
      jurisdictionManualId: selectedManualId,
      name: title,
      targetOrgId: currentOrganizationId || '',
    };

    setIsCreating(true);
    setServerErrors(undefined);

    createAgencyManual(
      userOrganizationId || '',
      payload,
      async (response) => {
        // Show success toast
        enqueueSnackbar(t('draftManuals.createAgencyManual.success', '', { manualName: title }), {
          variant: 'success',
        });

        // Wait for the query invalidation to complete before navigating
        await queryClient.invalidateQueries(['draft_manuals']);

        // Navigate to the newly created manual
        navigate(`..`);
      },
      (error) => {
        setServerErrors(error);
        setIsCreating(false);
      }
    );
  };

  // Show loading while fetching data
  if (draftManualsQuery.isLoading) {
    return (
      <TakeoverLayout
        pageTitle={t('draftManuals.createAgencyManual.title')}
        backUrl='..'
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </TakeoverLayout>
    );
  }

  return (
    <TakeoverLayout
      pageTitle={t('draftManuals.createAgencyManual.title')}
      backUrl='..'
    >
      <Box>
        <Card
          sx={{ maxWidth: 800, mx: 'auto', width: '100%' }}
          className='lex-form'
        >
          <Form onSubmit={handleSubmit}>
            <CardContent>
              {errorHelper.renderAllErrors(serverErrors)}
              <Stack spacing={4}>
                {/* Jurisdiction Manual Field - Required dropdown */}
                <FormControl
                  fullWidth
                  required
                >
                  <InputLabel id='manual-select-label'>{t('draftManuals.createAgencyManual.form.manual')}</InputLabel>
                  <Select
                    labelId='manual-select-label'
                    id='manual-select'
                    value={selectedManualId}
                    label={t('draftManuals.createAgencyManual.form.manual')}
                    onChange={(e) => setSelectedManualId(e.target.value)}
                    disabled={draftManualsQuery.isLoading}
                  >
                    {draftManualsQuery.isLoading ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} />
                        Loading jurisdiction manuals...
                      </MenuItem>
                    ) : (
                      jurisdictionManuals.map((manual: Manual) => (
                        <MenuItem
                          key={manual.id}
                          value={manual.id}
                        >
                          {manual.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>

                {/* Title Field - Editable */}
                <TextField
                  label={t('draftManuals.createAgencyManual.form.title')}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  fullWidth
                  required
                  helperText={t('draftManuals.createAgencyManual.form.titleHelper')}
                />
              </Stack>
            </CardContent>
            <CardActions>
              <Box />
              <Stack
                direction='row'
                spacing={2}
              >
                <Button
                  color='neutral'
                  href='..'
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  disabled={isCreating || isEmpty(selectedManualId) || isEmpty(title)}
                  component='button'
                  type='submit'
                  variant='contained'
                  endIcon={
                    isCreating ? (
                      <CircularProgress
                        size={20}
                        color='inherit'
                      />
                    ) : undefined
                  }
                >
                  {t('draftManuals.createAgencyManual.form.create')}
                </Button>
              </Stack>
            </CardActions>
          </Form>
        </Card>
      </Box>
    </TakeoverLayout>
  );
};

export default CreateAgencyManual;
